import AccountOptions from '@/models/AccountOptions'
import AccountOption from '@/api/models/AccountOption'
import SystemSettings from '@/models/SystemSettings'
import Account from '@/api/models/Account'
import { ExtraFields } from '@/models/ExtraFields'
import Connection from '@/api/models/Connection'
import { Brands } from '@/enum/Brands'
import { CdnHosts } from '@/enum/CdnHosts'

function isEmptyString (data: string): boolean {
    return data.trim().length <= 0
}

function getEmptyAccountOption(): AccountOption {
    return { code: '', value: null, type: '' }
}

function getDefaultAccount(): Account {
    return {
        cdekClientId: '',
        cdekClientSecret: '',
        isActive: false,
        isOrderWebhookEnabled: false,
        orderWebhookError: ''
    }
}

function getDefaultAccountOptions(): AccountOptions {
    return {
        itemNameAttributes: { code: '', value: [], type: '' },
        useTestServer: getEmptyAccountOption(),
        needAddInsurance: getEmptyAccountOption(),
        needAddCodCommission: getEmptyAccountOption(),
        codCommissionRate: getEmptyAccountOption(),
        prepaySumDistribution: getEmptyAccountOption(),
        sendLegalName: getEmptyAccountOption(),
        sellerNameMode: getEmptyAccountOption(),
        sellerCustomName: getEmptyAccountOption(),
        sellerNeedAdditionalInfo: getEmptyAccountOption(),
        needCallCourier: getEmptyAccountOption(),
        printInvoicesCount: getEmptyAccountOption(),
        printBarcodesCount: getEmptyAccountOption(),
        itemNameMode: getEmptyAccountOption(),
        sendItemArticle: getEmptyAccountOption(),
        senderNeedAdditionalInfo: getEmptyAccountOption(),
        senderNameMode: getEmptyAccountOption(),
        senderCustomName: getEmptyAccountOption(),
        needCheckDimensions: getEmptyAccountOption(),
        extendDeliveryDays: getEmptyAccountOption(),
        showAddCost: getEmptyAccountOption(),
        useFilterTariffs: getEmptyAccountOption(),
        senderContactInfoSource: getEmptyAccountOption(),
        deliveryRecipientCostFree: getEmptyAccountOption(),
        itemReturnedStatus: getEmptyAccountOption(),
        itemDeliveredStatus: getEmptyAccountOption(),
        itemFinallyReturnedStatus: getEmptyAccountOption(),
        deliveryPriceRoundingMode: getEmptyAccountOption(),
        paidPaymentStatus: getEmptyAccountOption(),
        customExecutorsList: { code: '', value: [], type: '' },
        needRegisterDelivery: getEmptyAccountOption(),
        paymentUpdateMode: getEmptyAccountOption(),
        barcodesPrintList: { code: '', value: [], type: '' },
        declaredValueMultiplier: getEmptyAccountOption(),
        freeTariffTypes: { code: '', value: [], type: '' },
        deliveryAsProduct: getEmptyAccountOption(),
        deliveryProductName: getEmptyAccountOption(),
        enableCdekPackage: getEmptyAccountOption(),
        enableOrderType: getEmptyAccountOption(),
        enableLoadingOperations: getEmptyAccountOption(),
        productProperty: getEmptyAccountOption(),
        defaultItemName: getEmptyAccountOption(),
        tariffSorting: getEmptyAccountOption(),
        recalculateCostByProperty: getEmptyAccountOption(),
        recalculateProperty: getEmptyAccountOption()
    }
}

function getDefaultSystemSettings(): SystemSettings {
    return {
        costCalculateBy: getEmptyAccountOption(),
        nullDeclaredValue: getEmptyAccountOption(),
        lockedByDefault: getEmptyAccountOption()
    }
}

function getEmptyExtraFields(): ExtraFields {
    return {
        deliveryExtraDataServices: { code: '', value: [], type: '' },
        deliveryExtraDataAddCost: getEmptyAccountOption(),
        deliveryExtraDataAddCostType: getEmptyAccountOption(),
        deliveryExtraDataAddOrderComment: getEmptyAccountOption(),
        shipmentExtraDataConsolidatedShipment: getEmptyAccountOption(),
        shipmentExtraDataExecutor: getEmptyAccountOption(),
        cdekPackageType: getEmptyAccountOption(),
        cdekPackageAmount: getEmptyAccountOption(),
        orderType: getEmptyAccountOption(),
        recipientContragentType: getEmptyAccountOption(),
        senderContragentType: getEmptyAccountOption(),
        shipmentExtraDataRequestToCourier: getEmptyAccountOption()
    }
}

function getBrandKey(connection: Connection): string {
    return connection.crmUrl.length === 0
        ? process.env.VUE_APP_BRAND_KEY || ''
        : connection.crmUrl.includes('.simla.com') ? Brands.simla : Brands.retailcrm;
}

function isSimla(connection: Connection): boolean {
    return getBrandKey(connection) === Brands.simla
}

function getHost(connection: Connection): string {
    return connection.crmUrl.length === 0
        ? process.env.VUE_APP_RETAILCRM_HOST || CdnHosts.retailcrm
        : (isSimla(connection)
            ? process.env.VUE_APP_SIMLA_HOST ?? CdnHosts.simla
            : process.env.VUE_APP_RETAILCRM_HOST ?? CdnHosts.retailcrm);
}

function setFaviconByConnection(connection: Connection): void {
    const brand = getBrandKey(connection);
    const elements = [
        {
            id: 'shortcut_favicon',
            value: '/favicon/favicon.ico'
        },
        {
            id: 'favicon',
            value: '/favicon/favicon.svg'
        },
        {
            id: 'apple_favicon',
            value: '/favicon/apple-touch-icon.png'
        },
        {
            id: 'manifest_favicon',
            value: '/favicon/manifest.json'
        }
    ];

    elements.forEach((element) => {
        const shortcutFavicon = document.getElementById(element.id);
        if (shortcutFavicon) {
            const shortcutFaviconHref = shortcutFavicon.attributes.getNamedItem('href');
            if (shortcutFaviconHref) {
                shortcutFaviconHref.value = '//' + getHost(connection) +
                    '/eu-central-1/retailcrm-static/branding/' + brand + element.value;
            }
        }
    });
}

export {
    isEmptyString,
    getDefaultAccountOptions,
    getDefaultSystemSettings,
    getEmptyExtraFields,
    getDefaultAccount,
    getBrandKey,
    isSimla,
    getHost,
    setFaviconByConnection
}
